import type { VxBadgeColor } from '@voxie/frontend-components';
import { pluralized } from '~/components/filters';
import type {
    RndConsentSource,
    RndRun,
    RndRunActor,
    RndRunCounts,
    RndRunEvent,
    RndRunEventName,
    RndRunNotChecked,
    RndRunReassigned,
    RndRunStatus,
    RndRunStopReason,
    RndRunTrigger,
} from '~/services/adminRndClient.types';
import { shortDateTime } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { formatNumber } from '~/utils/helpers';

const CALENDAR_DATE_FORMAT = 'YYYY-MM-DD';

const CANCELLABLE_STATUSES = [
    'applying',
    'awaiting_approval',
    'querying',
    'queued',
] as const satisfies readonly RndRunStatus[];

export type RndCancellableStatus = (typeof CANCELLABLE_STATUSES)[number];

export interface RndCountRow {
    key: string;
    label: string;
    count: number;
}

export interface RndFollowUpPlan {
    carriedResets: number;
    notYetQueried: number;
    error: number;
    changed: number;
    awaitingFollowUp: number;
    toQuery: number;
    rechecked: number;
}

export const runStatusLabels: Record<RndRunStatus, string> = {
    applying: 'Applying',
    awaiting_approval: 'Awaiting approval',
    cancelled: 'Cancelled',
    completed: 'Completed',
    completed_with_errors: 'Completed with errors',
    denied: 'Denied',
    expired: 'Expired',
    failed: 'Failed',
    partially_applied: 'Partially applied',
    querying: 'Querying',
    queued: 'Queued',
    stopped: 'Stopped',
    stopping: 'Stopping',
    superseded: 'Superseded',
};

export const runStatusColors: Record<RndRunStatus, VxBadgeColor> = {
    applying: 'sky',
    awaiting_approval: 'violet',
    cancelled: 'slate',
    completed: 'success',
    completed_with_errors: 'amber',
    denied: 'slate',
    expired: 'orange',
    failed: 'rose',
    partially_applied: 'amber',
    querying: 'sky',
    queued: 'slate',
    stopped: 'orange',
    stopping: 'amber',
    superseded: 'slate',
};

const runStatusOrder: RndRunStatus[] = [
    'queued',
    'querying',
    'awaiting_approval',
    'applying',
    'stopping',
    'completed',
    'completed_with_errors',
    'stopped',
    'partially_applied',
    'cancelled',
    'denied',
    'expired',
    'superseded',
    'failed',
];

export const runStatusOptions = runStatusOrder.map((value) => ({ value, label: runStatusLabels[value] }));

const stopReasonLabels: Record<RndRunStopReason, string> = {
    api_unavailable: 'API unavailable',
    auth_failed: 'Authentication failed',
    cancelled: 'Cancelled',
    company_id_rejected: 'Company ID rejected',
    out_of_credits: 'Out of credits',
};

const triggerLabels: Record<RndRunTrigger, string> = {
    ad_hoc: 'Ad hoc',
    follow_up: 'Follow-up',
    monthly: 'Monthly',
};

const runEventLabels: Record<RndRunEventName, string> = {
    applying: 'Applying',
    approved: 'Approved',
    awaiting_approval: 'Awaiting approval',
    cancelled: 'Cancelled',
    denied: 'Denied',
    expired: 'Expired',
    finished: 'Finished',
    followed_up: 'Followed up',
    loaded: 'Contacts loaded',
    started: 'Started',
    stopped: 'Stopped',
    superseded: 'Superseded',
};

export const consentSourceLabels: Record<RndConsentSource, string> = {
    attribute: 'Attribute',
    voxie: 'Voxie opt-in',
};

const reassignedLabels: Record<keyof RndRunReassigned, string> = {
    reset: 'Reset',
    active_since: 'Active since (left alone)',
    changed: 'Changed during the run',
    not_yet_applied: 'Not yet applied',
    awaiting_follow_up: 'Awaiting follow-up query',
};

const notCheckedLabels: Record<keyof RndRunNotChecked, string> = {
    not_yet_queried: 'Not yet queried',
    no_consent_date: 'No consent date',
    invalid_number: 'Invalid number',
    not_us: 'Not a US number',
    rejected: 'Rejected by RND',
    error: 'Error',
    no_longer_subscribed: 'No longer subscribed',
};

export const REVIEW_OFF_TEXT = 'Resets are applied as soon as querying ends.';

export const REVIEW_ON_TEXT =
    'The run queries and decides every contact, then waits for someone to approve or deny all of its resets. Nobody is reset before then, a run nobody decides on within 48 hours expires with nothing reset, and the queries stay billed either way.';

export const isRunStatus = (value: string): value is RndRunStatus => Object.hasOwn(runStatusLabels, value);

const isStopReason = (value: string): value is RndRunStopReason => Object.hasOwn(stopReasonLabels, value);

export const isCancellableStatus = (status: RndRunStatus): status is RndCancellableStatus =>
    (CANCELLABLE_STATUSES as readonly RndRunStatus[]).includes(status);

export const canCancelRun = (run: Pick<RndRun, 'status'>): boolean => isCancellableStatus(run.status);

export const canReviewRun = (run: Pick<RndRun, 'status'>): boolean => run.status === 'awaiting_approval';

const actorName = (actor: RndRunActor): string => actor.name ?? `User ${actor.id}`;

export const stopReasonLabel = (run: Pick<RndRun, 'stop_reason' | 'cancelled_by'>): string | null => {
    if (run.stop_reason === null) {
        return null;
    }

    if (run.stop_reason === 'cancelled' && run.cancelled_by !== null) {
        return `Cancelled by ${actorName(run.cancelled_by)}`;
    }

    return stopReasonLabels[run.stop_reason];
};

export const stopDetailLabel = (run: Pick<RndRun, 'stop_reason' | 'stop_detail'>): string | null => {
    if (run.stop_detail === null) {
        return null;
    }

    return run.stop_reason === 'out_of_credits'
        ? `${pluralized('query', Number(run.stop_detail), true)} remaining`
        : run.stop_detail;
};

export const runStatusDetail = (
    run: Pick<RndRun, 'status' | 'stop_reason' | 'cancelled_by' | 'approval_expires_at' | 'approved_by' | 'denied_by'>
): string | null => {
    if (run.status === 'awaiting_approval' && run.approval_expires_at !== null) {
        return `Expires ${shortDateTime(run.approval_expires_at)}`;
    }

    if (run.status === 'denied' && run.denied_by !== null) {
        return `Denied by ${actorName(run.denied_by)}`;
    }

    if (run.stop_reason !== null) {
        return stopReasonLabel(run);
    }

    return run.approved_by === null ? null : `Approved by ${actorName(run.approved_by)}`;
};

export const runByLabel = (run: Pick<RndRun, 'trigger' | 'user_id' | 'user_name'>): string => {
    const actor = run.user_id === null ? 'Schedule' : run.user_name ?? `User ${run.user_id}`;

    return `${triggerLabels[run.trigger]} · ${actor}`;
};

export const runTypeLabel = (run: Pick<RndRun, 'trigger'>): string =>
    run.trigger === 'follow_up' ? 'Follow-up' : 'Full';

export const calendarDateLabel = (value: string): string => {
    const date = dayjs(value, CALENDAR_DATE_FORMAT, true);

    return date.isValid() ? date.format('MMM Do, YYYY') : value;
};

export const runEventLabel = (event: Pick<RndRunEvent, 'event' | 'detail'>): string => {
    const label = runEventLabels[event.event];
    const detail = event.detail;

    if (detail === null) {
        return label;
    }

    if (event.event === 'finished' && isRunStatus(detail)) {
        return `${label}: ${runStatusLabels[detail]}`;
    }

    if (event.event === 'stopped' && isStopReason(detail)) {
        return `${label}: ${stopReasonLabels[detail]}`;
    }

    if (event.event === 'followed_up' || event.event === 'superseded') {
        return `${label} by run #${detail}`;
    }

    return label;
};

export const runEventActor = (event: Pick<RndRunEvent, 'user'>): string | null =>
    event.user === null ? null : actorName(event.user);

const breakdownRows = <TKey extends string>(
    labels: Record<TKey, string>,
    counts: Record<TKey, number>
): RndCountRow[] => (Object.keys(labels) as TKey[]).map((key) => ({ key, label: labels[key], count: counts[key] }));

export const reassignedRows = (breakdown: RndRunReassigned): RndCountRow[] =>
    breakdownRows(reassignedLabels, breakdown).filter((row) => row.key !== 'awaiting_follow_up' || row.count > 0);

export const notCheckedRows = (breakdown: RndRunNotChecked): RndCountRow[] =>
    breakdownRows(notCheckedLabels, breakdown);

export const followUpPlan = (counts: RndRunCounts | null): RndFollowUpPlan => {
    const reassigned = counts?.reassigned_breakdown;
    const notChecked = counts?.not_checked_breakdown;
    const notYetQueried = notChecked?.not_yet_queried ?? 0;
    const error = notChecked?.error ?? 0;
    const changed = reassigned?.changed ?? 0;
    const awaitingFollowUp = reassigned?.awaiting_follow_up ?? 0;

    return {
        carriedResets: reassigned?.not_yet_applied ?? 0,
        notYetQueried,
        error,
        changed,
        awaitingFollowUp,
        toQuery: notYetQueried + error + changed + awaitingFollowUp,
        rechecked:
            (notChecked?.no_consent_date ?? 0) +
            (notChecked?.invalid_number ?? 0) +
            (notChecked?.not_us ?? 0) +
            (notChecked?.no_longer_subscribed ?? 0),
    };
};

export const countLabel = (count: number | null | undefined): string =>
    count === null || count === undefined ? '-' : formatNumber(count);

export const contactsLabel = (count: number | null | undefined): string =>
    `${countLabel(count)} ${count === 1 ? 'contact' : 'contacts'}`;

export const runDateLabel = (value: string | null): string => (value === null ? '-' : shortDateTime(value));
